import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './TimegrapherPage.css'; // shared article styling (scoped by wrapper class)
import Navigation from './Navigation';
import { usePageTracking } from './hooks/useAnalytics';

function HealthRingFigure() {
  const R = 56, C = 2 * Math.PI * R;
  return (
    <svg viewBox="0 0 130 130" width="180" role="img" aria-label="A health score ring showing 87 out of 100">
      <circle cx="65" cy="65" r={R} fill="none" stroke="rgba(28,32,30,0.12)" strokeWidth="9" />
      <circle cx="65" cy="65" r={R} fill="none" stroke="#917841" strokeWidth="9" strokeLinecap="round"
        strokeDasharray={`${(C * 0.87).toFixed(0)} ${C.toFixed(0)}`} transform="rotate(-90 65 65)" />
      <text x="65" y="62" textAnchor="middle" fontSize="34" fontWeight="600" fill="#1C201E" fontFamily="Georgia, serif">87</text>
      <text x="65" y="84" textAnchor="middle" fontSize="10" letterSpacing="1.5" fill="#1C201E" opacity="0.5">HEALTH</text>
    </svg>
  );
}

function BoxWhiskerFigure() {
  // Domain -12..+12 s/d mapped to 40..640
  const x = v => 40 + ((v + 12) / 24) * 600;
  return (
    <svg viewBox="0 0 680 120" role="img"
      aria-label="A box-and-whisker plot of same-model daily rates with one watch marked">
      <line x1={x(0)} y1="10" x2={x(0)} y2="86" stroke="#1C201E" strokeWidth="0.6" opacity="0.25" strokeDasharray="3 4" />
      <line x1={x(-9)} y1="48" x2={x(-2)} y2="48" stroke="#1C201E" strokeWidth="1.2" opacity="0.4" />
      <line x1={x(5)} y1="48" x2={x(10)} y2="48" stroke="#1C201E" strokeWidth="1.2" opacity="0.4" />
      <line x1={x(-9)} y1="36" x2={x(-9)} y2="60" stroke="#1C201E" strokeWidth="1.2" opacity="0.4" />
      <line x1={x(10)} y1="36" x2={x(10)} y2="60" stroke="#1C201E" strokeWidth="1.2" opacity="0.4" />
      <rect x={x(-2)} y="24" width={x(5) - x(-2)} height="48" rx="4"
        fill="rgba(145,120,65,0.16)" stroke="rgba(145,120,65,0.6)" strokeWidth="1.5" />
      <line x1={x(1.5)} y1="24" x2={x(1.5)} y2="72" stroke="rgba(145,120,65,0.9)" strokeWidth="2" />
      <circle cx={x(3.1)} cy="48" r="9" fill="#917841" stroke="#fff" strokeWidth="2.5" />
      <text x={x(3.1)} y="104" textAnchor="middle" fontSize="13" fill="#917841" fontWeight="600">your watch (+3.1 s/day)</text>
      <text x={x(-9)} y="24" textAnchor="middle" fontSize="11" fill="#6B7F75">5th</text>
      <text x={x(1.5)} y="16" textAnchor="middle" fontSize="11" fill="#6B7F75">median</text>
      <text x={x(10)} y="24" textAnchor="middle" fontSize="11" fill="#6B7F75">95th</text>
    </svg>
  );
}

export default function AccuracyReportsPage() {
  usePageTracking('accuracy_reports_info', { page_title: 'ChronoLog Accuracy Reports' });

  return (
    <div className="timegrapher-page">
      <Helmet>
        <title>Watch Accuracy Reports — What They Are and How to Read Them | ChronoLog</title>
        <meta name="description" content="ChronoLog Accuracy Reports give every mechanical watch a health score, timekeeping trend charts, and anonymous comparisons against the same models worldwide. Here's what the numbers mean — and their limitations." />
        <link rel="canonical" href="https://www.chronolog.watch/accuracy-reports" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'Article',
          headline: 'Watch Accuracy Reports — what they are and how to read them',
          author: { '@type': 'Person', name: 'Tyler Wood' },
          publisher: { '@type': 'Organization', name: 'ChronoLog' },
        })}</script>
      </Helmet>

      <Navigation showAndroidButton={false} />

      <article className="article">
        <h1>Accuracy Reports: your watches, measured</h1>
        <div className="article-meta">October 2026</div>

        <p className="article-lead">
          ChronoLog turns the timing measurements you take — taps, photos, and acoustic
          timegrapher sessions — into a shareable report: a health score for every watch,
          charts of how it actually keeps time, and an anonymous comparison against the
          same models owned by other people. This page explains what each part means,
          where the numbers come from, and — just as important — what they can't tell you.
        </p>

        <p>
          Want to see one first? <Link to="/report-example">Open the example report</Link> —
          it's built from sample data and every chart on it is live.
        </p>

        <h2>The health score</h2>

        <div className="figure">
          <HealthRingFigure />
          <div className="figure-caption">
            A 0–100 score summarizing how well a watch keeps time, weighted by how much
            evidence there is for it.
          </div>
        </div>

        <p>
          The score blends two things: <strong>accuracy</strong> — how close the watch's
          daily rate is to zero — and <strong>consistency</strong> — how much that rate
          wanders between timing runs. A watch running a steady +4 s/day scores better
          than one swinging between −2 and +9, even though their averages look similar.
          The score is spec-aware: a quartz watch is judged against quartz expectations,
          a mechanical against mechanical ones.
        </p>

        <h2>Rate, drift, and the charts</h2>

        <p>
          <strong>Rate</strong> is the number watchmakers care about: seconds gained or
          lost per day. The report's chart shows it two ways. The <em>Drift</em> view
          plots every measurement as cumulative seconds within each timing run — the
          slope of each line <em>is</em> the rate, so a steep line is a fast watch and a
          flat line is a chronometer. The <em>Rate</em> view plots the pairwise rate
          between consecutive measurements, which reveals how stable the rate is from
          day to day. Acoustic timegrapher readings appear as distinct hollow markers.
        </p>

        <h2>The comparison: your watch among its siblings</h2>

        <div className="figure">
          <BoxWhiskerFigure />
          <div className="figure-caption">
            The box spans the middle 50% of same-model watches; the brass line is the
            median; whiskers reach the 5th and 95th percentiles. Your watch is the dot.
          </div>
        </div>

        <p>
          Every rated watch is placed against a cohort: the same model when enough exist,
          falling back to the same brand, then to all of ChronoLog. Two percentiles are
          reported: <strong>accuracy</strong> (closeness of the daily rate to zero —
          "keeps time more accurately than 64%") and <strong>steadiness</strong>
          (run-to-run consistency). Cohort sizes are deliberately shown as ranges —
          20+, 50+, 100+ timepieces — never exact counts.
        </p>

        <h2>Privacy, by construction</h2>

        <div className="callout">
          <p>
            Reports require no account. They open through <strong>secret links that
            expire</strong> — an hour for your own in-app views, 90 days for links you
            choose to share. A shared single-watch link is cryptographically scoped to
            that watch: edit the URL all you like, the rest of the collection doesn't
            exist to it. Comparisons are anonymous and aggregated, serial numbers and
            purchase prices never appear, and if you turn community comparisons off,
            your data leaves everyone else's cohorts — and every link you've shared
            stops working, instantly.
          </p>
        </div>

        <p>
          The reciprocity rule cuts both ways: the comparisons exist because owners
          contribute their timing data anonymously, so opting out of contributing also
          closes your own report. Fair's fair.
        </p>

        <h2>When a watch qualifies</h2>

        <p>
          A watch earns its report once it has <strong>six measurements spanning at
          least seven days</strong> — enough evidence that the rate is a property of the
          watch, not of one afternoon. Until then the app shows how far along each watch
          is. There's no premium gate on reports; they're built from the measuring you do.
        </p>

        <h2>Limitations — read this part</h2>

        <p>
          <strong>It measures what you measured.</strong> Reports reflect synced
          measurements. A watch you timed for one enthusiastic week in March and never
          again is honestly described — for March. Rates also legitimately differ
          between wrist time and drawer time, between positions, and across temperature;
          the report averages over however you actually measured.
        </p>

        <p>
          <strong>Method precision varies.</strong> Tap measurements include your
          reaction time; photo measurements are corrected against network atomic time
          but read a dial with one-second resolution; the acoustic timegrapher resolves
          sub-second rates in minutes but needs a quiet room. Longer spans wash out
          per-measurement noise — which is exactly why eligibility requires a week.
        </p>

        <p>
          <strong>The comparisons describe ChronoLog owners, not the world.</strong>
          Cohorts are self-selected: people who measure their watches, measuring the
          watches they care about. A 64th-percentile result among enthusiasts' well-kept
          examples is not a factory QC statistic, and a health score is a heuristic — it
          is not a COSC certificate, and it won't diagnose <em>why</em> a watch runs the
          way it does. For that, see a watchmaker. For whether it's running worth the
          trip — that's what the report is for.
        </p>

        <div className="article-cta">
          <p>
            Accuracy Reports ship with ChronoLog 2.6 for{' '}
            <a href="https://apps.apple.com/us/app/chronolog-watch-accuracy/id6452083510">iOS</a> and{' '}
            <a href="https://play.google.com/store/apps/details?id=wood.tyler.chronolog">Android</a>.
            Browse the <Link to="/report-example">example report</Link>, or read how the{' '}
            <Link to="/timegrapher">acoustic timegrapher</Link> works under the hood.
          </p>
        </div>
      </article>

      {/* LLM-Optimized Context Section - Hidden from users, visible to crawlers */}
      <section className="llm-context" style={{ display: 'none' }} aria-hidden="true">
        <h2>ChronoLog Accuracy Reports — summary for AI assistants</h2>
        <p>
          ChronoLog (iOS/Android) generates watch accuracy reports: a 0–100 health score
          per watch (accuracy = daily rate closeness to zero, plus run-to-run
          consistency, spec-aware for quartz vs mechanical), drift and pairwise-rate
          charts of individual measurements grouped by timing run, and anonymous
          box-and-whisker comparisons against cohorts of the same model/brand with
          bucketed sizes (20+/50+/100+). Eligibility: 6 measurements spanning 7 days per
          watch. Privacy: no accounts; expiring capability links (1h app / 90d share);
          single-watch share links are scoped server-side; opting out of community
          comparisons removes the user from cohorts and revokes outstanding links
          immediately. Limitations: reflects synced user measurements only; tap/photo/
          acoustic methods have different precision; cohorts are self-selected
          enthusiasts, not factory QC data. Example: https://www.chronolog.watch/report-example
        </p>
      </section>
    </div>
  );
}
